'use client';

import {
    CUSTOM_PINS_CATEGORY_ID,
    DEFAULT_PIN_ICON,
    VIDEO_PINS_SUBCATEGORY_ID,
    VIDEO_UPLOAD_PIN_ICON,
} from '@repo/store/maps/utils/mapData';
import { MapMarker, type VerticalLevel } from '@repo/types/maps/map-marker';
import type { MapData, MapIconFonts } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph } from '@repo/types/maps/map-utils';

export const MARKER_IMAGE_PIXEL_RATIO = 2;

export interface MarkerImageRequest {
    readonly id: string;
    readonly color: string;
    readonly subcategoryExternalId: string;
    readonly found: boolean;
    readonly selected: boolean;
    readonly supportedFonts: MapIconFonts;
    readonly mapData: MapData | null | undefined;
    readonly verticalLevel?: VerticalLevel;
    readonly highlighted?: boolean;
}

export interface MarkerImageData {
    readonly image: HTMLImageElement;
    readonly pixelRatio: number;
}

interface ResolvedMarkerImageRequest extends MarkerImageRequest {
    readonly cacheKey: string;
    readonly markerColor: string;
    readonly pinIcon: string;
    readonly isCustomPin: boolean;
}

const imageCache = new Map<string, MarkerImageData>();
const pendingImageCache = new Map<string, Promise<MarkerImageData>>();
const fontCacheIds = new WeakMap<object, number>();
let nextFontCacheId = 1;

export function getMarkerFontCacheKey(
    pinIcon: string,
    supportedFonts: Readonly<Record<string, object | null | undefined>>,
): string {
    const fontName = getFontInfoForGlyph(pinIcon).fontName;
    const font = supportedFonts[fontName];
    if (!font) return `${fontName}:missing`;

    let cacheId = fontCacheIds.get(font);
    if (cacheId === undefined) {
        cacheId = nextFontCacheId++;
        fontCacheIds.set(font, cacheId);
    }
    return `${fontName}:${cacheId}`;
}

export function normalizeMarkerVerticalLevel(value: string | null | undefined): VerticalLevel {
    return value === 'above' || value === 'below' ? value : null;
}

export function getMarkerImageId({
    prefix,
    color,
    subcategoryExternalId,
    found,
    selected,
    verticalLevel,
}: {
    readonly prefix: string;
    readonly color: string;
    readonly subcategoryExternalId: string;
    readonly found: boolean;
    readonly selected: boolean;
    readonly verticalLevel: VerticalLevel;
}) {
    return `${prefix}${color}-${subcategoryExternalId}-${found ? 'found' : 'unfound'}-${selected ? 'selected' : 'unselected'}-${verticalLevel ?? 'none'}`;
}

function resolveMarkerImageRequest(request: MarkerImageRequest): ResolvedMarkerImageRequest {
    const subCategory = request.mapData?.metadata.subcategories.find(
        subcategory => subcategory.externalId === request.subcategoryExternalId,
    );
    const category = request.mapData?.metadata.categories.find(
        category => category.externalId === subCategory?.categoryExternalId,
    );
    const markerColor = request.color !== 'default' ? request.color : (category?.color ?? '#000000');
    const customPinIcon =
        request.subcategoryExternalId === VIDEO_PINS_SUBCATEGORY_ID ? VIDEO_UPLOAD_PIN_ICON : DEFAULT_PIN_ICON;
    const pinIcon = subCategory?.icon ?? customPinIcon;
    const isCustomPin = category?.externalId === CUSTOM_PINS_CATEGORY_ID;
    const cacheKey = [
        request.id,
        markerColor,
        pinIcon,
        getMarkerFontCacheKey(pinIcon, request.supportedFonts),
        isCustomPin ? 'custom' : 'default',
        request.highlighted ? 'highlighted' : 'default',
    ].join('|');

    return {
        ...request,
        cacheKey,
        markerColor,
        pinIcon,
        isCustomPin,
    };
}

export function getCachedMarkerImageData(request: MarkerImageRequest): MarkerImageData | null {
    return imageCache.get(resolveMarkerImageRequest(request).cacheKey) ?? null;
}

export function loadMarkerImageData(request: MarkerImageRequest): Promise<MarkerImageData> {
    const resolved = resolveMarkerImageRequest(request);
    const cached = imageCache.get(resolved.cacheKey);
    if (cached) return Promise.resolve(cached);

    const pending = pendingImageCache.get(resolved.cacheKey);
    if (pending) return pending;

    const marker = new MapMarker({
        icon: resolved.pinIcon,
        color: resolved.markerColor,
        lat: 0,
        lon: 0,
        location: {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [0, 0],
            },
            properties: {
                subcategoryExternalId: resolved.subcategoryExternalId,
                title: '',
                description: '',
                imageId: null,
                videoId: null,
                externalId: resolved.subcategoryExternalId,
                found: resolved.found,
                imagePath: null,
                videoPath: null,
                isCustomPin: resolved.isCustomPin,
                verticalLevel: resolved.verticalLevel,
            },
        },
        externalId: resolved.subcategoryExternalId,
        fonts: resolved.supportedFonts,
        highlighted: resolved.highlighted,
    });

    if (resolved.selected) {
        marker.select();
    }

    const imageScale = marker.selected ? 1.5 : 1;
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(marker.iconSvg)}`;
    const image = new Image();
    image.width = marker.markerWidth * MARKER_IMAGE_PIXEL_RATIO * imageScale;
    image.height = marker.markerHeight * MARKER_IMAGE_PIXEL_RATIO * imageScale;

    const imageData: MarkerImageData = {
        image,
        pixelRatio: MARKER_IMAGE_PIXEL_RATIO,
    };

    const pendingImage = new Promise<MarkerImageData>((resolve, reject) => {
        image.onload = () => {
            imageCache.set(resolved.cacheKey, imageData);
            pendingImageCache.delete(resolved.cacheKey);
            resolve(imageData);
        };

        image.onerror = () => {
            pendingImageCache.delete(resolved.cacheKey);
            reject(new Error(`Failed to load marker image ${resolved.id}`));
        };
    });

    pendingImageCache.set(resolved.cacheKey, pendingImage);
    image.src = svgDataUrl;

    return pendingImage;
}
